import type { CalendarData, TweakPrefs, DragState } from '../modules/availability-calendar/types';
import { setPricingData, toYmd, addDays, dateRange, getDayPrice } from '../modules/availability-calendar/pricing';
import { initModals } from '../modules/availability-calendar/modals';
import { initDrag } from '../modules/availability-calendar/drag';
import { renderGrid } from '../modules/availability-calendar/grid';
import { initFilters, renderFilterBar, updateAccommodations, type FilterState } from '../modules/availability-calendar/filters';
import { loadPrefs, renderTweaksPanel } from '../modules/availability-calendar/tweaks';
import { getCellState } from '../modules/availability-calendar/bands';

const mount = document.getElementById('availability-calendar-mount');
if (mount) {
    initCalendar(mount);
}

function initCalendar(mount: HTMLElement): void {
    const dataUrl = mount.dataset.urlData ?? '';
    const contractCreateUrl = mount.dataset.urlContractCreate ?? '';
    const contractDetailsUrl = mount.dataset.urlContractDetails ?? '';
    const currency = mount.dataset.currency ?? 'RSD';
    const citiesRaw = mount.dataset.cities ?? '[]';
    const i18nRaw = mount.dataset.i18n ?? '{}';

    let cities: string[] = [];
    let i18n: Record<string, string> = {};
    try { cities = JSON.parse(citiesRaw); } catch (e) { console.warn('availability-calendar: failed to parse cities data', e); }
    try { i18n = JSON.parse(i18nRaw); } catch (e) { console.warn('availability-calendar: failed to parse i18n data', e); }

    const monthNames: string[] = (i18n['monthNames'] ?? 'January,February,March,April,May,June,July,August,September,October,November,December').split(',');

    let data: CalendarData | null = null;
    let prefs: TweakPrefs = loadPrefs();
    let drag: DragState | null = null;
    let filterState: FilterState | null = null;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayYmd = toYmd(today);
    const defaultFrom = toYmd(today);
    const defaultTo = toYmd(addDays(today, 27));

    // ── DOM structure ─────────────────────────────────────────────────────

    // Filter bar container (above card)
    const filterContainer = document.createElement('div');
    filterContainer.className = 'availability-calendar calendar-filter-outer';
    mount.appendChild(filterContainer);

    // Main card
    const mainCard = document.createElement('div');
    mainCard.className = `card border-0 shadow availability-calendar density-${prefs.density}`;
    mount.appendChild(mainCard);

    const cardHeader = document.createElement('div');
    cardHeader.className = 'calendar-card-header';
    // Week nav buttons
    cardHeader.innerHTML = `
        <button type="button" class="btn btn-sm btn-ghost js-prev-week">‹ ${i18n['week'] || 'Week'}</button>
        <button type="button" class="btn btn-sm btn-ghost js-today">${i18n['today'] || 'Today'}</button>
        <button type="button" class="btn btn-sm btn-ghost js-next-week">${i18n['week'] || 'Week'} ›</button>
    `;
    mainCard.appendChild(cardHeader);

    const cardBody = document.createElement('div');
    cardBody.className = 'card-body p-0 position-relative';
    mainCard.appendChild(cardBody);

    const scrollWrap = document.createElement('div');
    scrollWrap.className = 'tl-scroll';
    cardBody.appendChild(scrollWrap);

    const gridContainer = document.createElement('div');
    scrollWrap.appendChild(gridContainer);

    const statusEl = document.createElement('div');
    statusEl.className = 'calendar-status-msg d-none';
    cardBody.appendChild(statusEl);

    // Legend
    mainCard.appendChild(buildLegend(i18n));

    // Tweaks panel
    const tweaksRoot = document.createElement('div');
    mount.appendChild(tweaksRoot);

    // ── Initialize filters ────────────────────────────────────────────────

    filterState = initFilters(
        filterContainer,
        cities,
        defaultFrom,
        defaultTo,
        i18n,
        monthNames,
        (fs: FilterState) => {
            filterState = fs;
            fetchAndRender();
        }
    );

    renderFilterBar(filterContainer);

    // ── Week nav ──────────────────────────────────────────────────────────

    cardHeader.querySelector<HTMLButtonElement>('.js-prev-week')?.addEventListener('click', () => {
        if (!filterState) return;
        const f = new Date(filterState.from + 'T00:00:00');
        const t = new Date(filterState.to + 'T00:00:00');
        filterState = {
            ...filterState,
            from: toYmd(addDays(f, -7)),
            to: toYmd(addDays(t, -7)),
        };
        fetchAndRender();
    });
    cardHeader.querySelector<HTMLButtonElement>('.js-today')?.addEventListener('click', () => {
        if (!filterState) return;
        filterState = { ...filterState, from: defaultFrom, to: defaultTo };
        fetchAndRender();
    });
    cardHeader.querySelector<HTMLButtonElement>('.js-next-week')?.addEventListener('click', () => {
        if (!filterState) return;
        const f = new Date(filterState.from + 'T00:00:00');
        const t = new Date(filterState.to + 'T00:00:00');
        filterState = {
            ...filterState,
            from: toYmd(addDays(f, 7)),
            to: toYmd(addDays(t, 7)),
        };
        fetchAndRender();
    });

    // ── Modals ────────────────────────────────────────────────────────────

    initModals([], [], i18n, monthNames, contractCreateUrl, contractDetailsUrl, currency);

    // ── Drag ──────────────────────────────────────────────────────────────

    initDrag({
        getState: (unitId, dayIdx) => {
            if (!data || !filterState) return 'no-price';
            const days = currentDays();
            if (dayIdx < 0 || dayIdx >= days.length) return 'no-price';
            const ymd = toYmd(days[dayIdx]);
            return getCellState(unitId, ymd, todayYmd, data.bookings);
        },
        getDays: currentDays,
        getRowData: (unitId) => {
            const unit = data?.units.find(u => u.id === unitId);
            return unit ? { accomId: unit.accomId } : null;
        },
        redraw: (d) => {
            drag = d;
            renderCurrentGrid();
        },
    });

    // ── Tweaks ────────────────────────────────────────────────────────────

    renderTweaksPanel(tweaksRoot, prefs, i18n, (p) => {
        prefs = p;
        applyDensity(mainCard, prefs);
        renderCurrentGrid();
    });

    // ── Initial fetch ─────────────────────────────────────────────────────

    fetchAndRender();

    // ── Helpers ───────────────────────────────────────────────────────────

    function currentDays(): Date[] {
        if (!filterState) return dateRange(defaultFrom, defaultTo);
        return dateRange(filterState.from, filterState.to);
    }

    function fetchAndRender(): void {
        if (!filterState) return;
        const { cityId, offerIdFilter, from, to, showPartners, partnerAgencyFilter } = filterState;

        if (!cityId) {
            showStatus(i18n['selectCity'] || 'Select a city to view availability.');
            return;
        }

        setLoading(true);
        hideStatus();

        const params = new URLSearchParams({ city: cityId, from, to });
        if (offerIdFilter) params.set('offerId', String(offerIdFilter));
        if (!showPartners) params.set('includePartners', 'false');
        if (showPartners && partnerAgencyFilter != null) params.set('partnerAgencyId', String(partnerAgencyFilter));

        fetch(`${dataUrl}?${params.toString()}`)
            .then(r => r.json())
            .then((res: { success: boolean; data: CalendarData }) => {
                if (!res.success) throw new Error('API error');
                data = res.data;
                setPricingData(data.pricePeriods, data.bookings);
                initModals(data.units, data.accommodations, i18n, monthNames, contractCreateUrl, contractDetailsUrl, currency);
                updateAccommodations(data.accommodations);
                setLoading(false);
                renderCurrentGrid();
            })
            .catch((err: unknown) => {
                console.error('availability-calendar: fetch failed', err);
                setLoading(false);
                showStatus(i18n['loadError'] || 'Failed to load calendar data. Please try again.');
            });
    }

    function renderCurrentGrid(): void {
        if (!data || !filterState) return;

        const days = currentDays();
        const pricePeriods = data.pricePeriods;
        const priceValues = pricePeriods.map(p => p.perNight);
        const priceMin = priceValues.length > 0 ? Math.min(...priceValues) : 0;
        const priceMax = priceValues.length > 0 ? Math.max(...priceValues) : 0;

        const cellW = cellWFor(prefs.density);
        const labelW = labelWFor(prefs.density);
        scrollWrap.style.setProperty('--cell-w', `${cellW}px`);

        renderGrid({
            container: gridContainer,
            accoms: data.accommodations,
            units: data.units,
            bookings: data.bookings,
            days,
            todayYmd,
            prefs,
            i18n,
            drag,
            visibleStates: filterState.states,
            activeOnly: filterState.activeOnly,
            hasPriceOnly: filterState.hasPriceOnly,
            search: filterState.search,
            typeFilter: filterState.types,
            serviceFilter: filterState.services,
            classifFilter: filterState.classifs,
            priceMin,
            priceMax,
            cellW,
            labelW,
            currency,
        });

        const hasRows = gridContainer.querySelectorAll('.tl-row').length > 0;
        if (!hasRows) {
            showStatus(i18n['noUnitsFound'] || 'No units match the current filters.');
        } else {
            hideStatus();
        }
    }

    function setLoading(loading: boolean): void {
        gridContainer.classList.toggle('tl-loading', loading);
    }

    function showStatus(msg: string): void {
        gridContainer.innerHTML = '';
        statusEl.textContent = msg;
        statusEl.classList.remove('d-none');
    }

    function hideStatus(): void {
        statusEl.classList.add('d-none');
    }
}

function cellWFor(density: string): number {
    return density === 'compact' ? 32 : density === 'spacious' ? 56 : 42;
}

function labelWFor(density: string): number {
    return density === 'compact' ? 180 : 220;
}

function applyDensity(el: HTMLElement, prefs: TweakPrefs): void {
    el.classList.remove('density-compact', 'density-comfy', 'density-spacious');
    el.classList.add(`density-${prefs.density}`);
}

function buildLegend(i18n: Record<string, string>): HTMLElement {
    const legend = document.createElement('div');
    legend.className = 'legend availability-calendar';

    const items: Array<{ cls: string; label: string }> = [
        { cls: 'swatch booked',          label: i18n['booked']   || 'Booked' },
        { cls: 'swatch option',          label: i18n['optioned'] || 'Optioned' },
        { cls: 'swatch blocked',         label: i18n['blocked']  || 'Blocked' },
        { cls: 'swatch free-band-swatch', label: i18n['free']    || 'Free' },
        { cls: 'swatch no-price',        label: i18n['noPrice']  || 'No price' },
        { cls: 'swatch past',            label: i18n['past']     || 'Past' },
    ];

    for (const item of items) {
        const wrap = document.createElement('div');
        wrap.className = 'legend-item';
        wrap.innerHTML = `<span class="${item.cls}"></span><span>${item.label}</span>`;
        legend.appendChild(wrap);
    }

    const divider = document.createElement('span');
    divider.className = 'legend-divider';
    legend.appendChild(divider);

    const partnerItems: Array<{ cls: string; label: string }> = [
        { cls: 'swatch partner booked',           label: i18n['legendPartnerBooked'] || 'Partner booked' },
        { cls: 'swatch partner option',           label: i18n['legendPartnerOption'] || 'Partner optioned' },
        { cls: 'swatch partner free-band-swatch', label: i18n['legendPartnerFree']   || 'Partner free' },
    ];

    for (const item of partnerItems) {
        const wrap = document.createElement('div');
        wrap.className = 'legend-item';
        wrap.innerHTML = `<span class="${item.cls}"></span><span>${item.label}</span>`;
        legend.appendChild(wrap);
    }

    const hint = document.createElement('span');
    hint.className = 'legend-hint ms-auto';
    hint.textContent = i18n['dragHint'] || 'Drag across free cells to create a contract';
    legend.appendChild(hint);

    return legend;
}
